import { useState } from 'react';
import { ChevronRight, ChevronLeft, FileText, Bookmark } from 'lucide-react';
import { usePlayer } from '@/context/PlayerContext';
import { VoicePartBadge } from '@/components/ui/VoicePartBadges';
import { cn } from '@/lib/utils';

interface NowPlayingSidebarProps {
  className?: string;
}

export function NowPlayingSidebar({ className }: NowPlayingSidebarProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const {
    currentTrack,
    contentView,
    setContentView,
    seek,
  } = usePlayer();

  // Don't render if no track is playing
  if (!currentTrack) {
    return null;
  }

  if (isCollapsed) {
    return (
      <div className={cn(
        'w-12 bg-bg-elevated border-l border-subtle flex flex-col items-center py-4',
        className
      )}>
        <button
          onClick={() => setIsCollapsed(false)}
          className="p-2 rounded-full text-text-secondary hover:text-text-primary hover:bg-bg-elevated-hover transition-colors"
          title="Toon Now Playing"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        {/* Mini cover when collapsed */}
        {currentTrack.coverUrl && (
          <div className="mt-4">
            <img
              src={currentTrack.coverUrl}
              alt={currentTrack.title}
              className="w-8 h-8 rounded object-cover"
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={cn(
      'w-80 bg-bg-elevated border-l border-subtle flex flex-col overflow-hidden',
      className
    )}>
      {/* Header with collapse button */}
      <div className="flex items-center justify-between p-4 border-b border-subtle">
        <h2 className="font-semibold text-text-primary">Now Playing</h2>
        <button
          onClick={() => setIsCollapsed(true)}
          className="p-1.5 rounded-full text-text-secondary hover:text-text-primary hover:bg-bg-elevated-hover transition-colors"
          title="Verberg"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        {/* Cover Art */}
        <div className="p-4">
          {currentTrack.coverUrl ? (
            <img
              src={currentTrack.coverUrl}
              alt={currentTrack.title}
              className="w-full aspect-square rounded-lg object-cover shadow-lg"
            />
          ) : (
            <div className="w-full aspect-square rounded-lg bg-gradient-to-br from-zzing-purple-500/20 to-zzing-pink-500/20 flex items-center justify-center">
              <span className="text-6xl">♪</span>
            </div>
          )}
        </div>

        {/* Track Info */}
        <div className="px-4 pb-4">
          <h3 className="text-lg font-semibold text-text-primary truncate">
            {currentTrack.title}
          </h3>
          {currentTrack.artist && (
            <p className="text-sm text-text-secondary truncate">
              {currentTrack.artist}
            </p>
          )}
        </div>

        {/* Voice Part (if available) */}
        {currentTrack.trackName && (
          <div className="px-4 pb-4">
            <h4 className="text-xs font-medium text-text-muted uppercase tracking-wide mb-2">
              Stempartij
            </h4>
            <VoicePartBadge
              name={currentTrack.trackName}
              sortOrder={1} // TODO: Get actual sortOrder from track data
              compact={false}
            />
          </div>
        )}

        {/* Bookmarks/Cues (if available) */}
        {currentTrack.cues && currentTrack.cues.length > 0 && (
          <div className="px-4 pb-4">
            <h4 className="text-xs font-medium text-text-muted uppercase tracking-wide mb-2 flex items-center gap-1">
              <Bookmark className="w-3 h-3" />
              Markeringen
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {currentTrack.cues.slice(0, 6).map((cue) => (
                <button
                  key={cue.id}
                  onClick={() => seek(cue.timestamp)}
                  className="px-2 py-1 text-xs rounded-full bg-bg-base text-text-secondary hover:text-text-primary hover:bg-bg-elevated-hover transition-colors"
                  title={`Spring naar ${cue.name}`}
                >
                  {cue.name}
                </button>
              ))}
              {currentTrack.cues.length > 6 && (
                <span className="px-2 py-1 text-xs text-text-muted">
                  +{currentTrack.cues.length - 6}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Lyrics Preview / Open Lyrics */}
        <div className="px-4 pb-4">
          <h4 className="text-xs font-medium text-text-muted uppercase tracking-wide mb-2 flex items-center gap-1">
            <FileText className="w-3 h-3" />
            Songtekst
          </h4>

          {contentView === 'lyrics' ? (
            <button
              onClick={() => setContentView('default')}
              className="w-full px-3 py-2 text-sm rounded-lg bg-zzing-purple-500/20 text-zzing-purple-400 hover:bg-zzing-purple-500/30 transition-colors text-left"
            >
              ← Terug naar inhoud
            </button>
          ) : (
            <button
              onClick={() => setContentView('lyrics')}
              className="w-full px-3 py-2 text-sm rounded-lg bg-bg-base text-text-secondary hover:text-text-primary hover:bg-bg-elevated-hover transition-colors text-left"
            >
              Open songtekst →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
